import { List, Record } from 'immutable'
import { AssociatedContact, IAssociatedContact } from './AssociatedContact'
import {
  AssociatedDevelopmentClient,
  IAssociatedDevelopmentClient,
} from './AssociatedDevelopmentClient'
import {
  AssociatedDevelopmentSite,
  IAssociatedDevelopmentSite,
} from './AssociatedDevelopmentSite'
import { Attachment, IAttachment } from './Attachment'
import { EarnestMoney, IEarnestMoney } from './EarnestMoney'
import {
  DevelopmentPipelineType,
  DueDiligenceItemResponsibility,
  DueDiligenceItemType,
} from './enums'
import { IReminder, Reminder } from './Reminder'

export interface IDueDiligenceItem {
  readonly assignedTo: List<IAssociatedContact>
  readonly attachments: List<IAttachment>
  readonly client: IAssociatedDevelopmentClient
  readonly completedDate?: number
  readonly contacts: List<IAssociatedContact>
  readonly displayOrder: number
  readonly dueDate?: number
  readonly earnestMoney?: IEarnestMoney
  readonly id: string
  readonly isComplete: boolean
  readonly isCriticalDate: boolean
  readonly isPublic: boolean
  readonly key: string
  readonly parentKey?: string
  readonly pipelineType: DevelopmentPipelineType
  readonly reminders: List<IReminder>
  readonly responsibility?: DueDiligenceItemResponsibility
  readonly site: IAssociatedDevelopmentSite
  readonly title: string
  readonly type: DueDiligenceItemType
}

const DueDiligenceItemRecord = Record({
  assignedTo: List<AssociatedContact>(),
  attachments: List<Attachment>(),
  client: new AssociatedDevelopmentClient(),
  completedDate: undefined,
  contacts: List<AssociatedContact>(),
  displayOrder: 0,
  dueDate: undefined,
  earnestMoney: undefined,
  id: '',
  isComplete: false,
  isCriticalDate: false,
  isPublic: true,
  key: '',
  parentKey: undefined,
  pipelineType: DevelopmentPipelineType.Potential,
  reminders: List<Reminder>(),
  responsibility: undefined,
  site: new AssociatedDevelopmentSite(),
  title: '',
  type: DueDiligenceItemType.CheckOff,
})

export class DueDiligenceItem
  extends DueDiligenceItemRecord
  implements IDueDiligenceItem
{
  public static fromJS(initialState?: Partial<IDueDiligenceItem>) {
    if (!initialState) {
      return new DueDiligenceItem()
    }

    const { type } = initialState

    const {
      assignedTo,
      attachments,
      client,
      contacts,
      earnestMoney,
      reminders,
      site,
      ...rest
    } = initialState
    return new DueDiligenceItem({
      assignedTo: assignedTo
        ? List(assignedTo.map((a) => AssociatedContact.fromJS(a)))
        : List<AssociatedContact>(),
      attachments: attachments
        ? List(attachments.map((a) => Attachment.fromJS(a)))
        : List<Attachment>(),
      client: AssociatedDevelopmentClient.fromJS(client),
      contacts: contacts
        ? List(contacts.map((a) => AssociatedContact.fromJS(a)))
        : List<AssociatedContact>(),
      earnestMoney: earnestMoney
        ? EarnestMoney.fromJS(earnestMoney)
        : type === DueDiligenceItemType.EarnestMoney
        ? new EarnestMoney()
        : undefined,
      reminders: reminders
        ? List(reminders.map((r) => Reminder.fromJS(r)))
        : List<Reminder>(),
      site: AssociatedDevelopmentSite.fromJS(site),
      ...rest,
    })
  }

  public readonly assignedTo: List<AssociatedContact>
  public readonly attachments: List<Attachment>
  public readonly client: AssociatedDevelopmentClient
  public readonly completedDate?: number
  public readonly contacts: List<AssociatedContact>
  public readonly displayOrder: number
  public readonly dueDate?: number
  public readonly earnestMoney?: EarnestMoney
  public readonly id: string
  public readonly isComplete: boolean
  public readonly isCriticalDate: boolean
  public readonly isPublic: boolean
  public readonly key: string
  public readonly parentKey?: string
  public readonly pipelineType: DevelopmentPipelineType
  public readonly reminders: List<Reminder>
  public readonly responsibility?: DueDiligenceItemResponsibility
  public readonly site: AssociatedDevelopmentSite
  public readonly title: string
  public readonly type: DueDiligenceItemType

  constructor(props?: Partial<DueDiligenceItem>) {
    props ? super(props) : super()
  }
}
